'use client';

import { Icon } from '@/components/ui/Icon';
import { getStoreOpenStatus, getStoreWeekSchedule } from '@/lib/boutique/hours';
import { buildBoutiqueAddressLines, buildBoutiqueMapsUrl } from '@/lib/boutique/storeDisplayUtils';
import type { StoreHit } from '@/lib/algolia/stores';
import { getLanguageFromLocale } from '@/lib/i18n/config';
import { getTranslatedStoreValue } from '@/lib/stores/utils';
import { useLocale, useTranslations } from 'next-intl';

import {
  BoutiqueInfoAddressColumn,
  BoutiqueInfoAppointmentCta,
  BoutiqueInfoContactColumn,
  BoutiqueInfoHoursColumn,
  BoutiqueInfoServicesSection,
  type BoutiqueServiceItem,
} from './BoutiqueInfoColumns';
import styles from './BoutiqueInfo.module.scss';

export interface BoutiqueInfoProps {
  store: StoreHit;
  countryCode: string;
  cmsIndex?: number;
}

function ChevronLinkIcon() {
  return <Icon name="chevron-right" size={14} className={styles.linkIcon} aria-hidden="true" />;
}

function WatchServiceIcon({ className }: Readonly<{ className?: string }>) {
  return (
    <svg
      className={className}
      viewBox="0 0 40 40"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M23.9365 0.396484C24.903 0.396515 25.6865 1.18001 25.6865 2.14648V6.27637C25.6866 7.86476 26.1722 9.41532 27.0771 10.7207L26.9551 10.8047C27.7289 11.3909 28.4255 12.0726 29.0293 12.832L29.5791 12.5439C29.8234 12.4157 30.1254 12.5096 30.2539 12.7539L31.292 14.7285C31.4204 14.9727 31.327 15.2746 31.083 15.4033L30.668 15.6211C31.0267 16.4939 31.2807 17.42 31.416 18.3848H32.0234C32.2996 18.3848 32.5234 18.6086 32.5234 18.8848V21.1152C32.5234 21.3914 32.2996 21.6152 32.0234 21.6152H31.416C31.2758 22.6151 31.0095 23.5741 30.6299 24.4746L30.8584 24.5957C31.1027 24.7242 31.1967 25.0261 31.0684 25.2705L30.0303 27.2451C29.9017 27.4895 29.5989 27.5836 29.3545 27.4551L28.9639 27.249C28.313 28.0528 27.5562 28.766 26.7148 29.3701C26.0405 30.6476 25.6875 32.0726 25.6875 33.5205V37.7412C25.6874 38.7074 24.9037 39.4909 23.9375 39.4912H15.9785C15.0132 39.4912 14.2286 38.7097 14.2285 37.7422V33.8271C14.2285 32.0954 13.7059 30.404 12.7295 28.9736L12.7461 28.9609C10.138 26.8468 8.46982 23.6188 8.46973 20C8.46982 16.2091 10.2988 12.8448 13.123 10.7432C13.8474 9.42344 14.2275 7.94243 14.2275 6.43652V2.14551C14.2275 1.17801 15.0124 0.396725 15.9775 0.396484H23.9365ZM15.1504 30.4609C15.5302 31.5362 15.7285 32.6741 15.7285 33.8271V37.7422C15.7286 37.879 15.8394 37.9912 15.9785 37.9912H23.9375C24.0752 37.9909 24.1874 37.879 24.1875 37.7412V33.5205C24.1875 32.5161 24.3331 31.5213 24.6162 30.5664C23.2024 31.185 21.6418 31.5312 20 31.5312C18.2673 31.5312 16.6249 31.1457 15.1504 30.4609ZM20 9.96973C14.4605 9.96989 9.96989 14.4605 9.96973 20C9.96989 25.5395 14.4605 30.0311 20 30.0312C22.0614 30.0312 23.9766 29.4073 25.5703 28.3408C25.5731 28.3359 25.5763 28.3311 25.5791 28.3262L25.5869 28.3301C28.2666 26.5292 30.0311 23.471 30.0312 20C30.0311 14.4605 25.5395 9.96989 20 9.96973ZM24.9697 14.6309C25.2626 14.3382 25.7375 14.338 26.0303 14.6309C26.3226 14.9237 26.3228 15.3986 26.0303 15.6914L20.4863 21.2344L19.9561 21.7646L15.4668 17.2754C15.1739 16.9825 15.174 16.5077 15.4668 16.2148C15.7597 15.922 16.2345 15.922 16.5273 16.2148L19.9561 19.6436L24.9697 14.6309ZM15.9775 1.89648C15.8385 1.89672 15.7275 2.00873 15.7275 2.14551V6.43652C15.7275 7.47325 15.5714 8.49943 15.2715 9.4834C16.7146 8.83354 18.3147 8.46977 20 8.46973C21.6995 8.46977 23.312 8.83979 24.7646 9.5C24.3853 8.47355 24.1866 7.38288 24.1865 6.27637V2.14648C24.1865 2.00843 24.0746 1.89652 23.9365 1.89648H15.9775Z"
        fill="currentColor"
      />
    </svg>
  );
}

function StrapServiceIcon({ className }: Readonly<{ className?: string }>) {
  return (
    <svg
      className={className}
      viewBox="0 0 40 40"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M28.9446 9.70117C29.2398 9.46117 29.6747 9.47843 29.9485 9.75292L36.6067 16.4287C36.8986 16.7215 36.8971 17.1964 36.6038 17.4893L14.5179 39.5186C14.352 39.6836 14.1185 39.7626 13.887 39.7314L8.15457 38.9561C7.82717 38.9116 7.56743 38.6574 7.5159 38.3311L6.59012 32.4307C6.55288 32.1934 6.63184 31.9522 6.80204 31.7822L28.888 9.75292L28.9446 9.70117ZM8.13016 32.5713L8.91043 37.543L13.7239 38.1943L35.014 16.959L29.4124 11.3437L8.13016 32.5713ZM16.2337 28.3271C16.7079 27.853 17.4763 27.853 17.9505 28.3271C18.4246 28.8013 18.4246 29.5698 17.9505 30.0439C17.4763 30.5181 16.7079 30.518 16.2337 30.0439C15.7595 29.5697 15.7595 28.8013 16.2337 28.3271ZM19.1341 25.4268C19.6083 24.9526 20.3767 24.9526 20.8509 25.4268C21.325 25.901 21.325 26.6694 20.8509 27.1436C20.3767 27.6176 19.6082 27.6176 19.1341 27.1436C18.6599 26.6694 18.66 25.901 19.1341 25.4268ZM22.0345 22.5254C22.5086 22.0516 23.2772 22.0515 23.7513 22.5254C24.2254 22.9996 24.2254 23.769 23.7513 24.2432C23.2771 24.7171 22.5086 24.717 22.0345 24.2432C21.5603 23.769 21.5603 22.9996 22.0345 22.5254ZM19.8147 0.43066C20.1099 0.190339 20.5447 0.207825 20.8186 0.482417L27.1243 6.80468C27.2646 6.94533 27.3436 7.13607 27.3431 7.33496C27.3425 7.53374 27.2632 7.72464 27.1223 7.86523L15.1692 19.7871L10.7991 24.1455C10.607 24.3371 10.3265 24.411 10.0657 24.3379L8.89286 24.0088C6.30495 23.2824 4.2825 21.2646 3.54911 18.6787L3.2034 17.46C3.12862 17.1958 3.20428 16.9113 3.40067 16.7187L7.81082 12.3994L19.7581 0.482417L19.8147 0.43066ZM4.76688 17.4766L4.99149 18.2656C5.58292 20.3511 7.2149 21.9786 9.30204 22.5644L10.0511 22.7744L13.5794 19.2559L11.4847 17.1562L9.77665 18.8603L8.71805 17.7998L10.427 16.0957L8.32645 13.9902L4.76688 17.4766ZM9.39481 12.9346L14.6429 18.1963L15.8431 16.999L10.594 11.7373L9.39481 12.9346ZM11.6585 10.6768L16.9065 15.9385L25.5325 7.33496L20.2845 2.07324L11.6585 10.6768Z"
        fill="currentColor"
      />
    </svg>
  );
}

function BoutiqueServiceIcon({ className }: Readonly<{ className?: string }>) {
  return (
    <svg
      className={className}
      viewBox="0 0 40 40"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M36.3062 12.0713H34.4917V33.9033H37.4751L37.6489 34.3857L38.5962 37.0244L38.9468 38H1.05322L1.40381 37.0244L2.35107 34.3857L2.5249 33.9033H4.50342V12.0713H2.68896V2H36.3062V12.0713ZM3.12549 36.542H36.8745L36.4497 35.3604H3.55029L3.12549 36.542ZM15.4321 33.9033H23.564V12.0713H15.4321V33.9033ZM5.96143 33.7998H13.9751V12.0781H5.96143V33.7998ZM25.021 33.7998H33.0347V12.0781H25.021V33.7998ZM12.2612 25.8916H10.8042V21.6592H12.2612V25.8916ZM4.146 10.6133H34.8491V3.45703H4.146V10.6133Z"
        fill="currentColor"
      />
    </svg>
  );
}

function ServiceYourWatchIcon({ className }: Readonly<{ className?: string }>) {
  return (
    <svg className={className} viewBox="0 0 48 48" fill="none" aria-hidden="true">
      <path
        d="M18 30l-4 6h20l-4-6M24 10v8M20 18h8"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="24" cy="26" r="10" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}

function buildBoutiqueServices(
  store: StoreHit,
  labels: {
    buyingAWatch: string;
    buyingAStrap: string;
    pickUpInBoutique: string;
    serviceYourWatch: string;
  }
): BoutiqueServiceItem[] {
  const services: BoutiqueServiceItem[] = [
    { key: 'buyingAWatch', label: labels.buyingAWatch, icon: WatchServiceIcon },
    { key: 'buyingAStrap', label: labels.buyingAStrap, icon: StrapServiceIcon },
  ];

  if (store.offers_boutique_pickup) {
    services.push({
      key: 'pickUpInBoutique',
      label: labels.pickUpInBoutique,
      icon: BoutiqueServiceIcon,
    });
  }

  if (store.service_your_watch) {
    services.push({
      key: 'serviceYourWatch',
      label: labels.serviceYourWatch,
      icon: ServiceYourWatchIcon,
    });
  }

  return services;
}

/**
 * Address, contact, opening hours, appointment CTA, and services for boutique detail (CMS).
 */
export function BoutiqueInfo({ store, countryCode, cmsIndex = 0 }: Readonly<BoutiqueInfoProps>) {
  const locale = useLocale();
  const localeSlug = getLanguageFromLocale(locale);
  const t = useTranslations('boutiquesDetail');
  const tCommon = useTranslations('common');
  const openStatus = getStoreOpenStatus(store);
  const weekSchedule = getStoreWeekSchedule(store.hours);

  const street = getTranslatedStoreValue(store, 'street', localeSlug);
  const street2 = getTranslatedStoreValue(store, 'street2', localeSlug);
  const city = getTranslatedStoreValue(store, 'city', localeSlug);
  const region = getTranslatedStoreValue(store, 'region', localeSlug);
  const postalCode = getTranslatedStoreValue(store, 'postal_code', localeSlug);
  const countryLabel =
    new Intl.DisplayNames([localeSlug], { type: 'region' }).of(countryCode) ?? countryCode;

  const addressLines = buildBoutiqueAddressLines({
    countryCode,
    localeSlug,
    street,
    street2,
    city,
    region,
    postalCode,
  });

  const email = Array.isArray(store.emails) ? store.emails[0] : undefined;
  const mapsUrl = buildBoutiqueMapsUrl(store, street, city);

  const services = buildBoutiqueServices(store, {
    buyingAWatch: t('buyingAWatch'),
    buyingAStrap: t('buyingAStrap'),
    pickUpInBoutique: t('pickUpInBoutique'),
    serviceYourWatch: t('serviceYourWatch'),
  });

  const testId = (suffix: string) => `boutique-info-${cmsIndex}-${suffix}`;
  const chevronIcon = <ChevronLinkIcon />;

  return (
    <section
      className={styles.section}
      aria-labelledby={`boutique-info-heading-${cmsIndex}`}
      data-testid={testId('section')}
    >
      <h2 id={`boutique-info-heading-${cmsIndex}`} className={styles.visuallyHidden}>
        {t('infoHeading')}
      </h2>

      <div className={styles.columns}>
        <BoutiqueInfoAddressColumn
          addressLines={addressLines}
          countryLabel={countryLabel}
          mapsUrl={mapsUrl}
          directionsLabel={t('getDirections')}
          addressTitle={t('address')}
          testId={testId}
          chevronIcon={chevronIcon}
        />
        <BoutiqueInfoContactColumn
          phone={store.phone}
          email={email}
          phoneLabel={t('phone')}
          emailFieldLabel={t('email')}
          emailLabel={t('sendAnEmail')}
          contactTitle={t('contact')}
          testId={testId}
        />
        <BoutiqueInfoHoursColumn
          weekSchedule={weekSchedule}
          openStatus={openStatus}
          weekdayLabel={(weekday) => tCommon(`weekdays.${weekday}`)}
          openingHoursTitle={t('openingHours')}
          temporaryClosedLabel={t('temporaryClosed')}
          openNowLabel={t('openNow')}
          closedLabel={t('closed')}
        />
      </div>

      {store.offers_appointment ? (
        <BoutiqueInfoAppointmentCta
          storeId={store.store_id}
          label={t('bookAppointment')}
          testId={testId}
          chevronIcon={chevronIcon}
        />
      ) : null}

      <BoutiqueInfoServicesSection services={services} servicesHeading={t('servicesHeading')} />
    </section>
  );
}

BoutiqueInfo.displayName = 'BoutiqueInfo';
